import { useEffect, useState, useCallback } from "react";
import { Trash2, Mail, Phone, Package, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const STATUS = {
  new: { label: "Novo", cls: "bg-secondary/30 text-foreground" },
  contacted: { label: "Contactado", cls: "bg-[#FF9F1C]/20 text-[#b26a00]" },
  closed: { label: "Fechado", cls: "bg-[#7ED321]/20 text-[#4a7d0e]" },
};

export const OrdersPanel = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/orders").then((r) => { setOrders(r.data); setLoading(false); })
      .catch(() => { setLoading(false); toast.error("Não foi possível carregar os pedidos."); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    await api.patch(`/orders/${id}`, { status });
    setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, status } : o)));
    toast.success("Estado atualizado");
  };
  const remove = async (id) => {
    if (!window.confirm("Remover este pedido?")) return;
    await api.delete(`/orders/${id}`);
    setOrders((prev) => prev.filter((o) => o.id !== id));
    toast.success("Pedido removido");
  };

  return (
    <>
      <h1 className="font-display font-black text-3xl tracking-tight mb-1">Pedidos de orçamento</h1>
      <p className="text-muted-foreground mb-6">{orders.length} pedido(s) recebido(s)</p>

      {loading ? (
        <p className="text-muted-foreground">A carregar...</p>
      ) : orders.length === 0 ? (
        <div data-testid="no-orders" className="bg-card rounded-3xl border border-border p-12 text-center text-muted-foreground">
          <Package size={40} className="mx-auto mb-4 opacity-40" />
          Ainda não há pedidos. Quando um cliente enviar um pedido de orçamento, aparece aqui.
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((o) => (
            <div key={o.id} data-testid={`order-${o.id}`} className="bg-card rounded-3xl border border-border p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-display font-bold text-lg">{o.name}</h3>
                    <span className={`rounded-full text-xs font-bold px-3 py-1 ${STATUS[o.status]?.cls || ""}`}>{STATUS[o.status]?.label || o.status}</span>
                    <span title="Notificação para ti" className={`inline-flex items-center gap-1 text-xs ${o.email_sent ? "text-[#4a7d0e]" : "text-muted-foreground"}`}>
                      {o.email_sent ? <CheckCircle2 size={13} /> : <Clock size={13} />} {o.email_sent ? "Email enviado" : "Email não enviado"}
                    </span>
                    {/* Whether the customer was acknowledged. Worth showing
                        separately: if it did not go out, they are waiting
                        without knowing the request even arrived. */}
                    <span title="Confirmação para o cliente" className={`inline-flex items-center gap-1 text-xs ${o.customer_email_sent ? "text-[#4a7d0e]" : "text-muted-foreground"}`}>
                      {o.customer_email_sent ? <CheckCircle2 size={13} /> : <Clock size={13} />} {o.customer_email_sent ? "Cliente avisado" : "Cliente não avisado"}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mt-2">
                    <a href={`mailto:${o.email}`} className="inline-flex items-center gap-1 hover:text-primary"><Mail size={14} /> {o.email}</a>
                    {o.phone && <span className="inline-flex items-center gap-1"><Phone size={14} /> {o.phone}</span>}
                    <span>{new Date(o.created_at).toLocaleString("pt-PT")}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <select data-testid={`order-status-${o.id}`} value={o.status} onChange={(e) => setStatus(o.id, e.target.value)}
                    className="rounded-full border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/40">
                    <option value="new">Novo</option>
                    <option value="contacted">Contactado</option>
                    <option value="closed">Fechado</option>
                  </select>
                  <button data-testid={`order-delete-${o.id}`} onClick={() => remove(o.id)} className="p-2 rounded-full hover:bg-destructive/10 text-destructive"><Trash2 size={16} /></button>
                </div>
              </div>

              {o.message && <p className="text-sm mt-3 bg-muted/50 rounded-2xl p-3">{o.message}</p>}

              <div className="mt-3 flex flex-wrap gap-2">
                {o.items?.map((it, idx) => (
                  <span key={idx} className="inline-flex items-center gap-1 rounded-full bg-accent text-accent-foreground text-xs font-semibold px-3 py-1">
                    {it.name} <span className="opacity-70">×{it.quantity}</span>
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};
